/**
 * Hook to check Mobility MVP feature flag (global + per-user)
 * Checks both system_config global flag and profiles per-user flag
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

/**
 * Check Mobility MVP feature flag for current user
 */
export function useMobilityFlag(): {
  enabled: boolean;
  isLoading: boolean;
  error: Error | null;
} {
  const { user } = useAuth();
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['mobility-flag', user?.id],
    queryFn: async (): Promise<boolean> => {
      if (!user?.id) {
        return false;
      }
      
      // Check global flag first
      const { data: globalFlag } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'mobility_mvp_enabled')
        .maybeSingle();
      
      const globalEnabled = globalFlag?.value?.toLowerCase() === 'true';
      
      if (!globalEnabled) {
        return false;
      }
      
      // Check per-user flag
      const { data: profile } = await supabase
        .from('profiles')
        .select('enable_mobility_mvp')
        .eq('user_id', user.id)
        .maybeSingle();
      
      return profile?.enable_mobility_mvp === true;
    },
    enabled: !!user?.id,
    staleTime: 60000, // Cache for 1 minute
  });
  
  return {
    enabled: data ?? false,
    isLoading,
    error: error as Error | null
  };
}



